import { MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import { Badge, Box, Button, List, Stack, Typography } from '@mui/material';
// hooks
import useLocales from '../../../hooks/useLocales';
import useNotifications from '../../../hooks/useNotifications';
// components
import Scrollbar from '../../../components/Scrollbar';
import MenuPopover from '../../../components/MenuPopover';
import ErrorRetry from '../../../components/ErrorRetry';
import LoadingComponent from '../../../components/LoadingComponent';
import { IconButtonAnimate } from '../../../components/animate';
import NotificationRow from '../../../sections/main/notifications/NotificationRow';
import { InAppNotificationItem } from '../../../@types/notification.types';
import { APPOINTMENT_CALENDAR_COLORS } from '../../../theme/palette';
import { PATH_MAIN } from '../../../routes/paths';
import { resolveNotificationDestination } from '../../../utils/notificationUtils';

export default function NotificationsPopover() {
    const { translate } = useLocales();
    const navigate = useNavigate();
    const [open, setOpen] = useState<HTMLElement | null>(null);

    const {
        items,
        loading,
        error,
        totalUnreadCount,
        refetch,
        markRead,
        markAllSeen,
    } = useNotifications(undefined, { pollInterval: 60000 });

    const handleOpen = (event: MouseEvent<HTMLElement>) => {
        setOpen(event.currentTarget);
        void refetch();
        void markAllSeen();
    };

    const handleClose = () => {
        setOpen(null);
    };

    const handlePressItem = (item: InAppNotificationItem) => {
        markRead(item);

        const destination = resolveNotificationDestination(item);
        if (!destination) return;

        handleClose();
        void navigate(destination.pathname, destination.state ? { state: destination.state } : undefined);
    };

    const handleViewAll = () => {
        handleClose();
        navigate(PATH_MAIN.general.notifications);
    };

    return (
        <>
            <IconButtonAnimate
                color={open ? 'primary' : 'default'}
                onClick={handleOpen}
                aria-label={
                    totalUnreadCount > 0
                        ? translate('layouts.notificationsPopover.unreadMessages', {
                              count: totalUnreadCount,
                          })
                        : translate('layouts.notificationsPopover.notifications')
                }
                aria-haspopup="menu"
                aria-expanded={Boolean(open)}
                sx={{ width: 40, height: 40 }}
            >
                <Badge badgeContent={totalUnreadCount} color="error">
                    <Box
                        component="img"
                        src="/assets/reachlink-notifications.svg"
                        alt=""
                        aria-hidden
                        sx={{ width: 24, height: 24, display: 'block' }}
                    />
                </Badge>
            </IconButtonAnimate>

            <MenuPopover
                open={Boolean(open)}
                anchorEl={open}
                onClose={handleClose}
                disabledArrow
                sx={{
                    width: 360,
                    p: 2,
                    mt: 1.25,
                    ml: 0.75,
                    borderRadius: '12px',
                    boxShadow: `0 0 2px ${APPOINTMENT_CALENDAR_COLORS.shadow}, -20px 20px 40px -4px ${APPOINTMENT_CALENDAR_COLORS.shadow}`,
                    bgcolor: APPOINTMENT_CALENDAR_COLORS.white,
                }}
            >
                <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography
                            sx={{
                                fontSize: '16px',
                                lineHeight: '24px',
                                fontWeight: 600,
                                color: APPOINTMENT_CALENDAR_COLORS.textHeading,
                            }}
                        >
                            {translate('layouts.notificationsPopover.notifications')}
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleViewAll}
                            sx={{
                                minWidth: 65,
                                height: 30,
                                px: 1,
                                borderRadius: '8px',
                                borderColor: APPOINTMENT_CALENDAR_COLORS.outlinedButtonBorder,
                                color: APPOINTMENT_CALENDAR_COLORS.textPrimary,
                                fontSize: '13px',
                                lineHeight: '22px',
                                fontWeight: 700,
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: APPOINTMENT_CALENDAR_COLORS.outlinedButtonBorder,
                                    backgroundColor: APPOINTMENT_CALENDAR_COLORS.white,
                                },
                            }}
                        >
                            {translate('layouts.notificationsPopover.viewAll')}
                        </Button>
                    </Stack>

                    {loading && items.length === 0 && (
                        <Box sx={{ height: 160, position: 'relative' }}>
                            <LoadingComponent />
                        </Box>
                    )}

                    {error && items.length === 0 && (
                        <ErrorRetry error={error} onRetry={() => void refetch()} />
                    )}

                    {!loading && !error && items.length === 0 && (
                        <Box sx={{ px: 1.25, py: 1.5 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {translate('layouts.notificationsPopover.empty')}
                            </Typography>
                        </Box>
                    )}

                    {items.length > 0 && (
                        <Scrollbar sx={{ maxHeight: 340 }}>
                            <List
                                disablePadding
                                sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                            >
                                {items.map((item) => (
                                    <NotificationRow
                                        key={item.id}
                                        item={item}
                                        onPress={handlePressItem}
                                        dense
                                    />
                                ))}
                            </List>
                        </Scrollbar>
                    )}
                </Stack>
            </MenuPopover>
        </>
    );
}
